import packageJson from '../../../package.json';
import { HttpClientOptions, HttpClientResponse, GetHttpProps, CacheOptions } from './http-client.models';

const revalidate = 3600;
const defaultTimeout = 3000;

export const defaultHeaders = {
  'Content-Type': 'application/json; charset=utf-8',
  'X-App-Name': packageJson.name,
  'X-App-Version': packageJson.version,
};

class HttpClient {
  private _baseURL: HttpClientOptions['baseURL'];
  private _headers: HttpClientOptions['headers'];
  private _timeout: HttpClientOptions['timeout'];
  private _credentials: HttpClientOptions['credentials'];

  constructor({
    baseURL = '',
    headers = {},
    timeout = 3000,
    credentials = 'same-origin',
  }: Omit<HttpClientOptions, 'cache' | 'next'>) {
    this._baseURL = baseURL;
    this._headers = headers;
    this._timeout = timeout;
    this._credentials = credentials;
  }

  #overrideOptions = ({ baseURL, headers, timeout, credentials, cache }: HttpClientOptions): HttpClientOptions => ({
    baseURL: baseURL ?? this._baseURL,
    headers: headers ? { ...this._headers, ...headers } : this._headers,
    timeout: timeout ?? this._timeout,
    credentials: credentials ?? this._credentials,
    cache: cache ?? CacheOptions.Disabled,
    next: { revalidate },
  });

  #fetch = async <T>(url: string, options: RequestInit, timeout?: number): Promise<HttpClientResponse<T>> => {
    const controller = new AbortController();
    const { signal } = controller;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, { ...options, signal });
      const { headers, status, ok } = response;

      clearTimeout(timeoutId);

      if (!ok && status >= 400) {
        return {
          headers,
          status,
          data: null,
          error: await response.json(),
          exception: null,
        };
      }

      return {
        headers,
        status,
        data: await response.json(),
        error: null,
        exception: null,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      return {
        headers: null,
        status: null,
        data: null,
        error: null,
        exception: error,
      };
    }
  };

  get = <T>(endpiont: string, options: HttpClientOptions = {}): Promise<HttpClientResponse<T>> => {
    const { baseURL, headers, timeout, credentials, cache } = this.#overrideOptions(options);

    return this.#fetch(`${baseURL}${endpiont}`, { method: 'GET', headers, credentials, cache }, timeout);
  };

  post = <T>(endpiont: string, body: BodyInit, options: HttpClientOptions = {}): Promise<HttpClientResponse<T>> => {
    const { baseURL, headers, timeout, credentials, cache } = this.#overrideOptions(options);

    return this.#fetch(`${baseURL}${endpiont}`, { method: 'POST', headers, credentials, cache, body }, timeout);
  };

  put = <T>(endpiont: string, body: BodyInit, options: HttpClientOptions = {}): Promise<HttpClientResponse<T>> => {
    const { baseURL, headers, timeout, credentials, cache } = this.#overrideOptions(options);

    return this.#fetch(`${baseURL}${endpiont}`, { method: 'PUT', headers, credentials, cache, body }, timeout);
  };

  patch = <T>(endpiont: string, body: BodyInit, options: HttpClientOptions = {}): Promise<HttpClientResponse<T>> => {
    const { baseURL, headers, timeout, credentials, cache } = this.#overrideOptions(options);

    return this.#fetch(`${baseURL}${endpiont}`, { method: 'PATCH', headers, credentials, cache, body }, timeout);
  };

  delete = <T>(endpiont: string, options: HttpClientOptions = {}): Promise<HttpClientResponse<T>> => {
    const { baseURL, headers, timeout, credentials, cache } = this.#overrideOptions(options);

    return this.#fetch(`${baseURL}${endpiont}`, { method: 'DELETE', headers, credentials, cache }, timeout);
  };
}

export type HttpInstance = HttpClient;

export const httpServer: HttpInstance = new HttpClient({
  baseURL: process.env.NGINX_PROXY_PASS || '',
  headers: new Headers({ ...defaultHeaders }),
  timeout: defaultTimeout,
  credentials: 'same-origin',
});

export default function getHTTP({
  gatewayAddress,
  apiConnectionTimeout = null,
  requestHeaders = null,
}: GetHttpProps): HttpInstance {
  const headers = new Headers({
    ...defaultHeaders,
    // TODO: Fix that
    // eslint-disable-next-line unicorn/no-useless-fallback-in-spread
    ...(requestHeaders ?? {}),
  });
  const timeout = apiConnectionTimeout ?? defaultTimeout;

  return new HttpClient({
    baseURL: gatewayAddress,
    headers,
    timeout,
  });
}
